import { faPhone, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormField } from "../../components/ui/form-field";
import { InputGroup, InputGroupText } from "../../components/ui/input-group";
import { toast } from "../../components/ui/use-toast";
import {
  initValidation,
  validate,
  findErrors,
  isEmpty,
  isNotPhone,
  isNotEqualToField,
} from "../../hooks/formValidation";
import { CurrentSessionData } from "../../hooks/userSession";
import { authFetch } from "../../rest/authFetch";
import RestrictedAccess from "../../components/RestrictedAccess";

interface FoundCustomer {
  id: number;
  name: string;
  phone: string;
}

const UpdatePhone = ({
  currentSession,
}: {
  currentSession: CurrentSessionData;
}) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<"lookup" | "capture">("lookup");
  const [lookupPhone, setLookupPhone] = useState("");
  const [found, setFound] = useState<FoundCustomer | null>(null);
  const [newPhones, setNewPhones] = useState({
    newPhone: "",
    newPhoneConfirm: "",
  });
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [validation, setValidation] = useState(
    initValidation("phone", "newPhone", "newPhoneConfirm")
  );

  const validateLookup = (phone: string) => {
    const newValidation = findErrors(
      { phone },
      validate(
        "phone",
        isEmpty("Debe capturar un número de teléfono"),
        isNotPhone("El número de teléfono debe tener 10 dígitos")
      )
    );
    setValidation((current) => ({
      ...current,
      fields: { ...current.fields, phone: newValidation.fields["phone"] },
    }));
    return newValidation.isError;
  };

  const validateCapture = (phones: typeof newPhones) => {
    const newValidation = findErrors(
      phones,
      validate(
        "newPhone",
        isEmpty("Debe capturar el nuevo número de teléfono"),
        isNotPhone("El número de teléfono debe tener 10 dígitos")
      ),
      validate(
        "newPhoneConfirm",
        isEmpty("Debe confirmar el nuevo número de teléfono"),
        isNotEqualToField("newPhone", "Los números de teléfono no coinciden")
      )
    );
    setValidation((current) => ({
      ...current,
      fields: {
        ...current.fields,
        newPhone: newValidation.fields["newPhone"],
        newPhoneConfirm: newValidation.fields["newPhoneConfirm"],
      },
    }));
    return newValidation.isError;
  };

  const search = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validateLookup(lookupPhone)) {
      return;
    }

    setDisableSubmit(true);
    const url = `${process.env.REACT_APP_API_BASE_URL}/customer/search/q?phone=${lookupPhone}`;
    authFetch(url)
      .then((response) => response.json())
      .then((result) => {
        setDisableSubmit(false);
        const customers = result?.data?.customers ?? [];
        if (result?.success && customers.length > 0) {
          const customer = customers[0];
          setFound({
            id: customer.id,
            name: customer.customer_name,
            phone: customer.phone,
          });
          setStep("capture");
        } else {
          toast({
            variant: "destructive",
            title: "Mensaje",
            description: "No existe un cliente con ese número de teléfono",
          });
        }
      })
      .catch(() => {
        setDisableSubmit(false);
        toast({
          variant: "destructive",
          title: "Mensaje",
          description: "Ocurrió un error al buscar el cliente",
        });
      });
  };

  const update = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!found || validateCapture(newPhones)) {
      return;
    }

    const confirmed = window.confirm(
      `¿Confirma cambiar el teléfono de ${found.phone} a ${newPhones.newPhone}?`
    );
    if (!confirmed) {
      return;
    }

    setDisableSubmit(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        id: found.id,
        currentPhone: found.phone,
        newPhone: newPhones.newPhone,
      }),
    };
    authFetch(
      `${process.env.REACT_APP_API_BASE_URL}/customer/update-phone`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        setDisableSubmit(false);
        toast({
          variant: result.success ? "default" : "destructive",
          title: "Mensaje",
          description: result.message,
        });
        if (result.success) {
          reset();
        }
      })
      .catch(() => {
        setDisableSubmit(false);
        toast({
          variant: "destructive",
          title: "Mensaje",
          description: "Ocurrió un error al actualizar el teléfono",
        });
      });
  };

  const reset = () => {
    setStep("lookup");
    setLookupPhone("");
    setFound(null);
    setNewPhones({ newPhone: "", newPhoneConfirm: "" });
    setValidation(initValidation("phone", "newPhone", "newPhoneConfirm"));
  };

  const setNewPhoneField = (field: "newPhone" | "newPhoneConfirm", value: string) => {
    const updated = { ...newPhones, [field]: value };
    setNewPhones(updated);
  };

  return (
    <RestrictedAccess currentSession={currentSession}>
      <div className="space-y-4">
        <div>
          <h5 className="text-lg font-semibold">Actualizar Teléfono</h5>
          <hr className="my-2" />
        </div>

        {step === "lookup" && (
          <form noValidate onSubmit={search} className="space-y-4">
            <FormField>
              <Label htmlFor="phone">Teléfono actual del cliente:</Label>
              <InputGroup>
                <InputGroupText className="rounded-r-none border-r-0">
                  <FontAwesomeIcon icon={faPhone} />
                </InputGroupText>
                <Input
                  id="phone"
                  type="text"
                  placeholder="##########"
                  maxLength={10}
                  value={lookupPhone}
                  aria-invalid={validation.fields["phone"].isError}
                  className={
                    validation.fields["phone"].isError
                      ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                      : "rounded-l-none"
                  }
                  onChange={(event) => setLookupPhone(event.target.value)}
                />
              </InputGroup>
              {validation.fields["phone"].isError && (
                <p className="text-sm text-destructive">
                  {validation.fields["phone"].message}
                </p>
              )}
            </FormField>
            <hr className="my-2" />
            <div className="flex justify-center gap-4">
              <Button type="button" variant="secondary" onClick={() => navigate("/")}>
                Regresar
              </Button>
              <Button type="submit" disabled={disableSubmit}>
                Buscar
              </Button>
            </div>
          </form>
        )}

        {step === "capture" && found && (
          <form noValidate onSubmit={update} className="space-y-4">
            <FormField>
              <Label>Cliente:</Label>
              <InputGroup>
                <InputGroupText className="rounded-r-none border-r-0">
                  <FontAwesomeIcon icon={faUser} />
                </InputGroupText>
                <Input
                  type="text"
                  value={`${found.name} (${found.phone})`}
                  className="rounded-l-none"
                  disabled
                />
              </InputGroup>
            </FormField>
            <FormField>
              <Label htmlFor="newPhone">Nuevo teléfono:</Label>
              <InputGroup>
                <InputGroupText className="rounded-r-none border-r-0">
                  <FontAwesomeIcon icon={faPhone} />
                </InputGroupText>
                <Input
                  id="newPhone"
                  type="text"
                  placeholder="##########"
                  maxLength={10}
                  value={newPhones.newPhone}
                  aria-invalid={validation.fields["newPhone"].isError}
                  className={
                    validation.fields["newPhone"].isError
                      ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                      : "rounded-l-none"
                  }
                  onChange={(event) => setNewPhoneField("newPhone", event.target.value)}
                />
              </InputGroup>
              {validation.fields["newPhone"].isError && (
                <p className="text-sm text-destructive">
                  {validation.fields["newPhone"].message}
                </p>
              )}
            </FormField>
            <FormField>
              <Label htmlFor="newPhoneConfirm">Confirmar nuevo teléfono:</Label>
              <InputGroup>
                <InputGroupText className="rounded-r-none border-r-0">
                  <FontAwesomeIcon icon={faPhone} />
                </InputGroupText>
                <Input
                  id="newPhoneConfirm"
                  type="text"
                  placeholder="##########"
                  maxLength={10}
                  value={newPhones.newPhoneConfirm}
                  aria-invalid={validation.fields["newPhoneConfirm"].isError}
                  className={
                    validation.fields["newPhoneConfirm"].isError
                      ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                      : "rounded-l-none"
                  }
                  onChange={(event) =>
                    setNewPhoneField("newPhoneConfirm", event.target.value)
                  }
                />
              </InputGroup>
              {validation.fields["newPhoneConfirm"].isError && (
                <p className="text-sm text-destructive">
                  {validation.fields["newPhoneConfirm"].message}
                </p>
              )}
            </FormField>
            <hr className="my-2" />
            <div className="flex justify-center gap-4">
              <Button type="button" variant="secondary" onClick={reset}>
                Regresar
              </Button>
              <Button type="submit" disabled={disableSubmit}>
                Actualizar
              </Button>
            </div>
          </form>
        )}
      </div>
    </RestrictedAccess>
  );
};

export default UpdatePhone;
